'use client';

import {
  useState
} from 'react';

import {
  useRouter
} from 'next/navigation';

import {
  sendPasswordResetEmail
} from 'firebase/auth';

import {
  getAuthErrorCode,
  toFriendlyAuthError,
  userFacingError
} from '@repo/auth/auth/friendly-error';

import {
  auth
} from '@repo/auth';

import {
  AUTH_ROUTES
} from '@repo/auth/zone-urls';

import
  Script
from 'next/script';

import {
  requestPasswordResetEmail
} from '@repo/api-client/user-management-service';

import {
  getRecaptchaToken
} from '@repo/ui/recaptcha-token';

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

import {
  AuthCard,
  AuthInput,
  AuthButton,
  AuthError,
  AuthLink,
  AuthPolicyLinks
} from '@/app/ui/auth';

// Platform-sent reset email (branded, links to our /auth/action): Firebase's
// own templates are locked on the prod project (2026-10-05). The platform
// answers the same for any address. If it cannot send (reCAPTCHA blocked,
// not configured, network), Firebase sends its default email instead.
async function sendResetLink(email: string): Promise<void> {

  try {

    const recaptchaToken = await getRecaptchaToken(undefined, 'password_reset');
    await requestPasswordResetEmail(email, recaptchaToken);
    return;
  } catch {

    // fall through to Firebase
  }

  try {

    await sendPasswordResetEmail(auth, email);
  } catch (error) {

    // Same answer whether or not the address has an account.
    if (getAuthErrorCode(error) === 'auth/user-not-found') {

      return;
    }

    throw toFriendlyAuthError(error, 'password-reset-request');
  }
}

export default function ForgotPasswordPage() {

  const router = useRouter();
  const [ email, setEmail ] = useState('');
  const [ confirmEmail, setConfirmEmail ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState('');
  const [ successEmail, setSuccessEmail ] = useState<string | null>(null);

  const clearError = () => setError('');

  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault();
    clearError();
    const norm = email.trim().toLowerCase();
    const normConfirm = confirmEmail.trim().toLowerCase();

    if (!norm) {

      setError('Please enter your email address.');
      return;
    }

    if (!normConfirm) {

      setError('Please confirm your email address.');
      return;
    }

    if (norm !== normConfirm) {

      setError('Email addresses do not match.');
      return;
    }

    try {

      setLoading(true);
      await sendResetLink(norm);
      setSuccessEmail(norm);
    } catch (err) {

      setError(userFacingError(err, 'We could not send the reset email just now. Please try again in a moment.', 'password-reset-request'));
    } finally {

      setLoading(false);
    }
  };

  if (successEmail) {

    return (
      <AuthCard>
        <h1 className="text-2xl font-light text-[#111111] dark:text-white mb-1">Check your email</h1>
        <p className="text-sm text-gray-400 mb-6">
          We have sent a password reset link to
          {' '}
          <span className="font-medium text-[#111111] dark:text-white">{successEmail}</span>
          .
        </p>
        <div className="space-y-4">
          <p className="text-sm text-gray-500">It can take a few minutes to arrive. If you can&apos;t see it, check your Junk or Spam folder, or try again with the email address you registered with.</p>
          <AuthButton type="button" onClick={() => router.push(AUTH_ROUTES.login)}>Back to login</AuthButton>
        </div>
        <AuthPolicyLinks />
      </AuthCard>
    );
  }

  return (
    <AuthCard footer={(
      <p className="text-center text-sm text-gray-500">
        Remembered your password?
        {' '}
        <AuthLink href={AUTH_ROUTES.login}>Back to login</AuthLink>
      </p>
    )}
    >
      {/* reCAPTCHA v3 for the platform reset endpoint; the token is fetched on
          submit by getRecaptchaToken, which waits for this script. */}
      {recaptchaSiteKey && (
        <Script src={`https://www.google.com/recaptcha/api.js?render=${ recaptchaSiteKey }`} strategy="afterInteractive" />
      )}
      <h1 className="text-2xl font-light text-[#111111] dark:text-white mb-1">Forgot password</h1>
      <p className="text-sm text-gray-400 mb-7">Enter your email and we will send you a password reset link.</p>

      <AuthError message={error} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {

            setEmail(e.target.value);
            clearError();
          }}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
        <AuthInput
          id="confirmEmail"
          label="Confirm Email"
          type="email"
          value={confirmEmail}
          onChange={(e) => {

            setConfirmEmail(e.target.value);
            clearError();
          }}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
        <AuthButton loading={loading}>{loading ? 'Sending...' : 'Send reset link'}</AuthButton>
      </form>
    </AuthCard>
  );
}
